// BSIForfaitPrompt — Popup demandant quel forfait BSI (BSA/BSB/BSC) appliquer
// lorsqu'un soin de nursing est sélectionné sans forfait déjà choisi.

import { BSI_FORFAITS } from '../data/careData';

interface BSIForfaitPromptProps {
  open: boolean;
  onSelect: (forfaitId: string) => void;
  onClose: () => void;
}

const FORFAIT_DETAILS: Record<string, { gir: string; desc: string; accent: string }> = {
  'bsi-bsa': { gir: 'GIR 4', desc: 'Dépendance légère', accent: 'bg-primary-500' },
  'bsi-bsb': { gir: 'GIR 3', desc: 'Dépendance intermédiaire', accent: 'bg-secondary-500' },
  'bsi-bsc': { gir: 'GIR 1-2', desc: 'Dépendance lourde', accent: 'bg-accent-500' },
};

export default function BSIForfaitPrompt({ open, onSelect, onClose }: BSIForfaitPromptProps) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Fond assombri */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      ></div>

      {/* Carte */}
      <div className="relative w-full max-w-md bg-white rounded-2xl border border-background-200/60 p-6 animate-result-enter">
        {/* En-tête */}
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
            <i className="ri-brain-line text-primary-600 text-lg"></i>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold font-heading text-foreground-900">
              Quel forfait BSI appliquer ?
            </h3>
            <p className="text-xs text-foreground-500 leading-relaxed mt-1">
              Ce soin fait partie du forfait BSI. Choisissez le forfait correspondant au niveau de dépendance du patient.
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Fermer"
            className="w-7 h-7 rounded-lg flex items-center justify-center text-foreground-300 hover:text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer flex-shrink-0"
          >
            <i className="ri-close-line text-base"></i>
          </button>
        </div>

        {/* Choix du forfait */}
        <div className="mt-4 space-y-2">
          {BSI_FORFAITS.map((forfait) => {
            const detail = FORFAIT_DETAILS[forfait.id];
            return (
              <button
                key={forfait.id}
                onClick={() => onSelect(forfait.id)}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-background-200/70 bg-background-50 hover:border-primary-300 hover:bg-primary-50/40 transition-all cursor-pointer text-left group"
              >
                <span className={`w-1.5 h-9 rounded-full ${detail.accent} flex-shrink-0`}></span>
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-bold text-foreground-900">{forfait.label}</span>
                  <span className="block text-[11px] text-foreground-500">
                    {detail.gir} · {detail.desc}
                  </span>
                </div>
                <span className="text-sm font-bold text-foreground-900 whitespace-nowrap">{forfait.montant}</span>
                <i className="ri-arrow-right-line text-foreground-300 group-hover:text-primary-500 text-base"></i>
              </button>
            );
          })}
        </div>

        {/* Ignorer */}
        <button
          onClick={onClose}
          className="mt-4 w-full py-2.5 rounded-xl text-xs font-medium text-foreground-400 hover:text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
        >
          Plus tard — je choisis le forfait moi-même
        </button>
      </div>
    </div>
  );
}